/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */

import { __ } from './language.js';

/**
 * Represents the right-to-left text direction.
 * @constant {string}
 */
export const DIRECTION_RTL = 'rtl';

/**
 * Represents the left-to-right text direction.
 * @constant {string}
 */
export const DIRECTION_LTR = 'ltr';

// known list of language codes for RTL scripts
const rtlCodes = [
    'ae', // Avestan
    'ar', // Arabic
    'arc', // Aramaic
    'bcc', // Southern Balochi
    'bqi', // Bakthiari
    'ckb', // Sorani
    'dv', // Divehi
    'fa', // Persian
    'glk', // Gilaki
    'ha', // Hausa
    'he', // Hebrew
    'khw', // Khowar
    'ks', // Kashmiri
    'ku', // Kurdish
    'mzn', // Mazanderani
    'nqo', // N'Ko
    'pnb', // Western Punjabi
    'ps', // Pashto
    'sd', // Sindhi
    'ug', // Uyghur
    'ur', // Urdu
    'yi' // Yiddish
];

// set of internal helpers for making sure the keys are not case-sensitive
const normalize = text => `${text}`.trim().toLowerCase();
const normalizeLanguage = lang => normalize(lang).replace(/[_\s]+/g, '-');
const normalizeLanguageDirection = direction => {
    if (normalize(direction) === DIRECTION_RTL) {
        return DIRECTION_RTL;
    }
    return DIRECTION_LTR;
};

// internal cache for the mapping override
let languageDirectionMapping = {};

/**
 * Sets the text direction for a particular language code.
 * @param {string} lang - The language code for which set the direction. It is NOT case-sensitive.
 * @param {string} direction - The text direction to assign to the language.
 */
export const setLanguageDirection = (lang, direction) => {
    languageDirectionMapping[normalizeLanguage(lang)] = normalizeLanguageDirection(direction);
};

/**
 * Gets the text direction for a particular language code.
 * @param {string} lang - The language code for which get the direction. It is NOT case-sensitive.
 * @returns {string} - The text direction assigned to the language.
 */
export const getLanguageDirection = lang => {
    const normalizedLang = normalizeLanguage(lang);
    if (languageDirectionMapping[normalizedLang]) {
        return languageDirectionMapping[normalizedLang];
    }

    const languageCode = normalizedLang.split('-')[0];
    if (rtlCodes.includes(languageCode)) {
        return DIRECTION_RTL;
    }
    return DIRECTION_LTR;
};

/**
 * Set the mapping defining the direction for each language
 * @param {object} newMapping - An object mapping language code with a direction (`rtl` or `ltr`)
 */
export const setLanguageDirectionMapping = newMapping => {
    if (typeof newMapping !== 'object') {
        throw new TypeError('A language direction mapping must be defined with an object!');
    }

    languageDirectionMapping = Object.keys(newMapping).reduce((mapping, lang) => {
        mapping[normalizeLanguage(lang)] = normalizeLanguageDirection(newMapping[lang]);
        return mapping;
    }, {});
};

/**
 * Tells if a given language needs to be written right-to-left.
 * @param {string} lang - The language code to check
 * @returns {boolean} - Returns `true` if the language is written right-to-left ; otherwise returns `false`.
 */
export const isLanguageRTL = lang => getLanguageDirection(lang) === DIRECTION_RTL;

/**
 * Tells if the current locale needs to be written right-to-left.
 * @returns {boolean} - Returns `true` if the language is written right-to-left ; otherwise returns `false`.
 */
export const isLocaleRTL = () => isLanguageRTL(__.getLocale());

/**
 * Gets the text direction for the current locale.
 * @returns {string} - The text direction assigned to the language.
 */
export const getLocaleDirection = () => getLanguageDirection(__.getLocale());

/**
 * Extract language code according to ISO 639: ar-AR --> ar; en-US --> en; nb-NO --> nb
 * @param {string} lang - The language code for which get the direction.
 * @returns {string} - The text direction assigned to the language.
 */
export const getLanguageShortCode = lang => {
    const normalizedLang = normalizeLanguage(lang);
    const languageCode = normalizedLang.split('-')[0];
    return languageCode;
};
